import type { FormField } from '@re/form-engine';
import { FIELD_TYPES } from '@re/form-engine';
import { tr } from '../../lib/i18n';

/**
 * The form as a JSON file: a full copy of every field (rules, options, config) — to keep a copy,
 * to compare versions, or to prepare a version outside the builder and load it into a draft.
 */
export function downloadFormJson(formName: string, version: number | null, isDraft: boolean, fields: FormField[]): void {
  const doc = { kind: 'formtags-form', form: formName, version, draft: isDraft, exportedAt: new Date().toISOString(), fields };
  const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  const safe = formName.replace(/[\\/:*?"<>|\s]+/g, '-');
  a.download = `${safe}-v${version ?? 0}${isDraft ? '-draft' : ''}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

/**
 * Reads fields from a file made by «تنزيل JSON», a bare array of fields, or the public form API
 * response ({ form: { schema: { fields } } }). Checks the shape only — the builder's own
 * validation (and the server on save) checks the rules.
 */
export async function readFormJson(file: File): Promise<FormField[]> {
  let data: unknown;
  try {
    data = JSON.parse(await file.text());
  } catch {
    throw new Error(tr('الملف ليس JSON صالحاً', 'The file is not valid JSON'));
  }
  const d = data as { fields?: unknown; form?: { schema?: { fields?: unknown } }; schema?: { fields?: unknown } };
  const raw = Array.isArray(data) ? data : (d.fields ?? d.form?.schema?.fields ?? d.schema?.fields);
  if (!Array.isArray(raw) || raw.length === 0) throw new Error(tr('لم أجد قائمة حقول في الملف', 'No list of fields found in the file'));
  const types = new Set<string>(FIELD_TYPES);
  const bad = raw.findIndex((f) => !f || typeof f !== 'object' || typeof f.key !== 'string' || !types.has(f.type) || typeof f.label?.ar !== 'string');
  if (bad >= 0) throw new Error(tr(`الحقل رقم ${bad + 1} غير صالح (key / type / label)`, `Field #${bad + 1} is not valid (key / type / label)`));
  return (raw as FormField[])
    .map((f, i) => ({
      key: f.key,
      type: f.type,
      section: f.section ?? null,
      order: typeof f.order === 'number' ? f.order : (i + 1) * 10,
      label: f.label,
      required: !!f.required,
      requiredWhen: f.requiredWhen ?? null,
      visibleWhen: f.visibleWhen ?? null,
      mapsTo: f.mapsTo ?? null,
      config: f.config ?? {},
    }))
    .sort((a, b) => a.order - b.order);
}
